<template>
  <div class="color-pair-picker">
    <v-card variant="outlined" rounded="lg" class="pa-3">

      <!-- Label + hint -->
      <div class="d-flex align-center justify-space-between mb-2">
        <span class="text-subtitle-2 font-weight-bold">{{ label }}</span>
        <span class="text-caption text-medium-emphasis">{{ hint }}</span>
      </div>

      <!-- Preview swatch: surface + on-surface text sample -->
      <div
        class="swatch-preview rounded mb-3 d-flex align-center justify-center"
        :style="{ background: surfaceColor, minHeight: '56px' }"
      >
        <span
          v-if="!single"
          class="text-body-2 font-weight-medium px-2"
          :style="{ color: onColor }"
        >Aa 123</span>
      </div>
      <div
        v-if="showContrastCheck"
        class="contrast-check mt-3 pa-2 rounded"
        :class="contrastCheck.passesAllLevels ? 'contrast-check--pass' : 'contrast-check--fail'"
        role="status"
        aria-live="polite"
      >
        <div class="d-flex align-center justify-space-between gap-2 flex-wrap mb-1">
          <span class="text-caption font-weight-bold">ADA/WCAG contrast</span>
          <span class="text-caption font-weight-bold">{{ contrastCheck.ratio }}:1</span>
        </div>

        <div class="d-flex gap-2 flex-wrap">
          <span class="contrast-badge" :class="contrastCheck.passesLargeAA ? 'contrast-badge--pass' : 'contrast-badge--fail'">
            {{ contrastCheck.passesLargeAA ? '✓' : '×' }} AA large
          </span>
          <span class="contrast-badge" :class="contrastCheck.passesAA ? 'contrast-badge--pass' : 'contrast-badge--fail'">
            {{ contrastCheck.passesAA ? '✓' : '×' }} AA normal
          </span>
          <span class="contrast-badge" :class="contrastCheck.passesAAA ? 'contrast-badge--pass' : 'contrast-badge--fail'">
            {{ contrastCheck.passesAAA ? '✓' : '×' }} AAA normal
          </span>
        </div>
      </div>
      <v-row dense align="center">
        <!-- Large: surface color -->
        <v-col :cols="single ? 12 : 8">
          <p class="text-caption mb-1" style="line-height:1.2">
            Surface<br /><code class="text-caption">{{ surfaceColor }}</code>
          </p>
          <div class="d-flex align-center gap-2">
            <!-- Native color input as trigger -->
            <label class="color-swatch border-thin border-on-background" :style="{ background: surfaceColor }">
              <input
                type="color"
                :value="surfaceColor"
                class="hidden-color-input"
                @input="e => $emit('update:surfaceColor', e.target.value)"
              />
            </label>
            <v-text-field
              :model-value="surfaceColor"
              density="compact"
              variant="outlined"
              hide-details
              maxlength="7"
              style="font-size:12px; max-width: 110px;"
              @update:model-value="val => isValidHex(val) && $emit('update:surfaceColor', val)"
            />
          </div>
        </v-col>

        <!-- Small: on-surface (text) color -->
        <v-col v-if="!single" cols="4">
          <p class="text-caption mb-1" style="line-height:1.2">
            Text<br /><code class="text-caption">{{ onColor }}</code>
          </p>
          <div class="d-flex align-center gap-2 flex-wrap">
            <label class="color-swatch color-swatch--sm border-thin border-on-background" :style="{ background: onColor }">
              <input
                type="color"
                :value="onColor"
                class="hidden-color-input"
                @input="e => $emit('update:onColor', e.target.value)"
              />
            </label>
          </div>
        </v-col>
      </v-row>

      

    </v-card>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  label: { type: String, required: true },
  hint: { type: String, default: '' },
  surfaceColor: { type: String, required: true },
  onColor: { type: String, default: '' },
  surfaceVar: { type: String, required: true },
  onVar: { type: String, default: '' },
  /** When true, hides the on-color picker and shows only the surface swatch. */
  single: { type: Boolean, default: false },
})

defineEmits(['update:surfaceColor', 'update:onColor'])

const contrastCheck = computed(() => {
  const ratio = getContrastRatio(props.surfaceColor, props.onColor)

  if (!ratio) {
    return null
  }

  return {
    ratio: ratio.toFixed(2),
    passesLargeAA: ratio >= 3,
    passesAA: ratio >= 4.5,
    passesAAA: ratio >= 7,
    passesAllLevels: ratio >= 7,
  }
})

const showContrastCheck = computed(() => !props.single && contrastCheck.value && !contrastCheck.value.passesAllLevels)

function isValidHex(val) {
  return /^#[0-9A-Fa-f]{6}$/.test(val)
}

function getContrastRatio(surfaceColor, onColor) {
  if (!isValidHex(surfaceColor) || !isValidHex(onColor)) {
    return null
  }

  const surfaceLuminance = getRelativeLuminance(surfaceColor)
  const onLuminance = getRelativeLuminance(onColor)
  const lighter = Math.max(surfaceLuminance, onLuminance)
  const darker = Math.min(surfaceLuminance, onLuminance)

  return (lighter + 0.05) / (darker + 0.05)
}

function getRelativeLuminance(hexColor) {
  const [red, green, blue] = hexColor
    .slice(1)
    .match(/.{2}/g)
    .map(channel => getLinearRgb(parseInt(channel, 16) / 255))

  return (0.2126 * red) + (0.7152 * green) + (0.0722 * blue)
}

function getLinearRgb(channel) {
  return channel <= 0.03928
    ? channel / 12.92
    : ((channel + 0.055) / 1.055) ** 2.4
}
</script>

<style scoped>
.color-pair-picker {
  width: 100%;
}

.swatch-preview {
  border: 1px solid rgb(var(--v-theme-on-customizer-surface) / 0.1);
}

/* Clickable color swatch that wraps a native <input type="color"> */
.color-swatch {
  display: inline-block;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid rgb(var(--v-theme-on-customizer-surface) / 0.3);
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.color-swatch--sm {
  width: 28px;
  height: 28px;
}

.hidden-color-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
}

.contrast-check {
  border: 1px solid rgb(var(--v-theme-on-customizer-surface) / 0.12);
  background: rgb(var(--v-theme-on-customizer-surface) / 0.03);
}

.contrast-check--pass {
  border-color: rgb(var(--v-theme-customizer-success) / 0.35);
  background: rgb(var(--v-theme-customizer-success) / 0.08);
}

.contrast-check--fail {
  border-color: rgb(var(--v-theme-customizer-error) / 0.35);
  background: rgb(var(--v-theme-customizer-error) / 0.08);
}

.contrast-badge {
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  line-height: 1.4;
  font-weight: 600;
}

.contrast-badge--pass {
  color: rgb(var(--v-theme-customizer-success));
  background: rgb(var(--v-theme-customizer-success) / 0.14);
}

.contrast-badge--fail {
  color: rgb(var(--v-theme-customizer-error));
  background: rgb(var(--v-theme-customizer-error) / 0.14);
}
</style>
